import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth, ROLE_LABEL } from "@/lib/auth";
import { Plus, Upload, Download, FileText, LogOut, UserX } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const ROLES = ["telecaller", "executive", "branch_manager", "admin", "super_admin"];

function NewEmployeeDialog({ maxRole }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "", role: "executive", branch: "Siddipet", password: "" });
  const mut = useMutation({
    mutationFn: async () => (await api.post("/employees", f)).data,
    onSuccess: () => { toast.success("Employee created"); qc.invalidateQueries({ queryKey: ["employees"] }); setOpen(false); setF({ name: "", email: "", phone: "", role: "executive", branch: "Siddipet", password: "" }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const allowed = ROLES.slice(0, ROLES.indexOf(maxRole) + 1);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="bg-[#0052FF] hover:bg-[#003FCC] text-white text-sm font-semibold px-4 py-2 rounded-full inline-flex items-center gap-1.5" data-testid="new-emp-btn"><Plus size={14} /> New Employee</button>
      </DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle>New Employee</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); mut.mutate(); }} className="space-y-3" data-testid="new-emp-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name *</Label><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="emp-name" /></div>
            <div><Label>Email *</Label><Input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="emp-email" /></div>
            <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
            <div><Label>Branch</Label><Input value={f.branch} onChange={(e) => setF({ ...f, branch: e.target.value })} /></div>
            <div>
              <Label>Role</Label>
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                <SelectTrigger data-testid="emp-role"><SelectValue /></SelectTrigger>
                <SelectContent>{allowed.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Temp Password *</Label><Input type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="emp-password" /></div>
          </div>
          <button type="submit" disabled={mut.isPending} className="w-full bg-[#0052FF] hover:bg-[#003FCC] text-white font-semibold px-4 py-2.5 rounded-full" data-testid="emp-submit">Create Employee</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UploadAssetDialog({ empId, kind, onDone }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const doUpload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a file"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/employees/${empId}/upload/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${kind} uploaded`);
      setOpen(false); setFile(null); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="text-[10px] text-[#0052FF] hover:text-[#003FCC] inline-flex items-center gap-1 font-semibold" data-testid={`upload-${kind}-${empId}`}><Upload size={10} /> {kind}</button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader><DialogTitle>Upload {kind}</DialogTitle></DialogHeader>
        <form onSubmit={doUpload} className="space-y-3">
          <Input type="file" onChange={(e) => setFile(e.target.files?.[0])} data-testid={`upload-${kind}-file`} />
          <button type="submit" disabled={busy} className="w-full bg-[#0052FF] hover:bg-[#003FCC] text-white font-semibold px-4 py-2.5 rounded-full" data-testid={`upload-${kind}-submit`}>{busy ? "Uploading…" : "Upload"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResignDialog({ empId, isSelf }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/employees/${empId}/resign`, { resignation_feedback: feedback })).data,
    onSuccess: () => { toast.success("Resignation recorded"); qc.invalidateQueries({ queryKey: ["employees"] }); setOpen(false); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="text-[10px] text-red-700 hover:text-red-900 inline-flex items-center gap-1 font-semibold" data-testid={`resign-${empId}`}><UserX size={10} /> Resign</button>
      </DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle>{isSelf ? "Submit Resignation" : "Record Employee Resignation"}</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); if (feedback.length < 10) { toast.error("Feedback (min 10 chars) required"); return; } mut.mutate(); }} className="space-y-3">
          <div>
            <Label>Resignation Feedback *</Label>
            <Textarea rows={5} value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="Reason & feedback about your experience…" data-testid="resign-feedback" />
          </div>
          <button type="submit" disabled={mut.isPending} className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2.5 rounded-full" data-testid="resign-submit">{mut.isPending ? "Submitting…" : "Submit Resignation"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

async function downloadProfile(empId, employeeId) {
  try {
    const res = await api.get(`/employees/${empId}/profile-pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `employee_${employeeId || empId}.pdf`; a.click();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error("Download failed"); }
}

export default function Employees() {
  const { user } = useAuth();
  const [includeResigned, setIncludeResigned] = useState(false);
  const qc = useQueryClient();
  const { data: employees = [] } = useQuery({
    queryKey: ["employees", includeResigned],
    queryFn: async () => (await api.get("/employees", { params: { include_resigned: includeResigned } })).data,
  });
  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#06152D]">Employees</h1>
          <p className="text-sm text-[#52525B]">Manage users, resume/KYC/signature uploads, resignations, and profile PDFs.</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs text-[#52525B] inline-flex items-center gap-1">
            <input type="checkbox" checked={includeResigned} onChange={(e) => setIncludeResigned(e.target.checked)} data-testid="show-resigned" /> Show resigned
          </label>
          {canWrite(user) && <NewEmployeeDialog maxRole={user.role} />}
        </div>
      </div>

      <div className="mt-6 bg-white border border-[#E4E4E7] rounded-xl overflow-hidden">
        <table className="w-full lj-table" data-testid="employees-table">
          <thead><tr><th>Emp ID</th><th>Name</th><th>Email</th><th>Role</th><th>Branch</th><th>Status</th><th>Uploads</th><th>Joined</th><th>Actions</th></tr></thead>
          <tbody>
            {employees.length === 0 && <tr><td colSpan="9" className="text-center text-[#52525B] py-8">No employees.</td></tr>}
            {employees.map((e) => (
              <tr key={e.id} data-testid={`emp-row-${e.id}`}>
                <td className="font-mono-num text-xs font-semibold">{e.employee_id || "—"}</td>
                <td className="font-medium">{e.name}</td>
                <td className="text-xs">{e.email}</td>
                <td><Badge variant="secondary" className="bg-[#EBF2FF] text-[#0052FF]">{ROLE_LABEL[e.role]}</Badge></td>
                <td>{e.branch || "—"}</td>
                <td>
                  {e.resigned ? <Badge variant="secondary" className="bg-red-100 text-red-800">Resigned</Badge> :
                    e.active ? <Badge className="bg-emerald-100 text-emerald-800" variant="secondary">Active</Badge> : <Badge variant="secondary">Inactive</Badge>}
                </td>
                <td>
                  <div className="flex gap-2 flex-wrap">
                    <UploadAssetDialog empId={e.id} kind="resume" onDone={() => qc.invalidateQueries({ queryKey: ["employees"] })} />
                    <UploadAssetDialog empId={e.id} kind="kyc" onDone={() => qc.invalidateQueries({ queryKey: ["employees"] })} />
                    <UploadAssetDialog empId={e.id} kind="signature" onDone={() => qc.invalidateQueries({ queryKey: ["employees"] })} />
                    {e.resume_document_id && <span className="text-[9px] text-emerald-700">✓resume</span>}
                    {e.kyc_document_id && <span className="text-[9px] text-emerald-700">✓kyc</span>}
                    {e.signature_document_id && <span className="text-[9px] text-emerald-700">✓sig</span>}
                  </div>
                </td>
                <td className="text-xs text-[#52525B]">{new Date(e.created_at).toLocaleDateString("en-IN")}</td>
                <td>
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={() => downloadProfile(e.id, e.employee_id)} className="text-[10px] text-[#0052FF] hover:text-[#003FCC] inline-flex items-center gap-1 font-semibold" data-testid={`profile-pdf-${e.id}`}>
                      <Download size={10} /> Profile PDF
                    </button>
                    {!e.resigned && (user?.role === "super_admin" || user?.role === "admin" || user?.id === e.id) && (
                      <ResignDialog empId={e.id} isSelf={user?.id === e.id} />
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function canWrite(u) { return u && ["admin", "super_admin"].includes(u.role); }
